import { Fragment, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api/client";
import type { Project } from "../api/types";
import { ProjectTabs, tabPath } from "./ProjectTabs";
import { Icon } from "./icons";

// The shared project top bar — one white bar across every project page (Proofing,
// Spec check…), mirroring the chapter report's head so the two views feel like
// one app: project name + workflow tabs on the LEFT, and a page-supplied actions
// cluster on the RIGHT (the project burger leads it; the primary action sits at
// the far right, like "＋ Flag here" on the report).
//
// No back arrow here on purpose. It led to the projects list, which is already
// one click away in the left nav — and next to the project name it read as
// "back to the project", not "leave the project". The burger still carries an
// explicit "All projects" for anyone who wants it.
export function ProjectHeader({
  projectId,
  name,
  active,
  right,
}: {
  projectId: string;
  name: string;
  active: string; // workflow key of the page rendering the bar
  right?: ReactNode;
}) {
  return (
    <div className="topbar">
      <div className="topbar-left">
        <ProjectSwitcher projectId={projectId} name={name} active={active} />
        <span className="topbar-div" aria-hidden="true" />
        <ProjectTabs projectId={projectId} active={active} />
      </div>
      {right != null && <div className="topbar-right">{right}</div>}
    </div>
  );
}

// The switcher's groups: the Workflows board's statuses, in the board's own
// order (pages/Workflows.tsx STATUS / STATUS_ORDER - keep the two in step),
// then the projects that aren't on the board at all.
const GROUPS: { key: string; label: string; dot: string }[] = [
  { key: "in_progress", label: "In Progress", dot: "#2540F6" },
  { key: "assigning", label: "Assigning", dot: "#8312E0" },
  { key: "confirm", label: "Confirm", dot: "#E0890A" },
  { key: "on_hold", label: "On Hold", dot: "#E23054" },
  { key: "pending_chain", label: "Pending Chain", dot: "#6B72D4" },
  { key: "announced", label: "Announced", dot: "#8A90AC" },
  { key: "complete", label: "Complete", dot: "#18A97A" },
  { key: "", label: "Not on the Workflows board", dot: "" },
];

// A book booked twice can sit in two statuses ("1st QC" complete, "2nd QC" in
// progress). It's listed once, under the one furthest from done - the job
// still needing attention is the one you'd be switching to it for.
function switcherGroup(p: Project): number {
  const idx = (p.workflow_statuses ?? [])
    .map((s) => GROUPS.findIndex((g) => g.key === s))
    .filter((i) => i >= 0);
  return idx.length ? Math.min(...idx) : GROUPS.length - 1;
}

// The title doubles as a jump to another project, so moving between books no
// longer means a trip back through Workflows. Each one is named with the
// service it's booked for ("Dead to Us · 1st QC") because the same book often
// runs as two jobs, and the name alone can't tell them apart. The closed button
// carries the current project's service too, so a glance at the bar answers
// "which job am I in?" without opening anything.
function ProjectSwitcher({ projectId, name, active }: { projectId: string; name: string; active: string }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [hi, setHi] = useState(0);
  const [pos, setPos] = useState({ top: 52, left: 16 });
  const btnRef = useRef<HTMLButtonElement>(null);
  const navigate = useNavigate();
  // The sidebar's own query — same key, same cache, so opening this costs
  // nothing and it stays in step with every archive/rename that invalidates it.
  const { data: projects } = useQuery({
    queryKey: ["projects", "active"],
    queryFn: () => api<{ projects: Project[] }>("/api/projects").then((r) => r.projects),
  });

  // Grouped the way the Workflows board groups them, A-Z inside each group.
  // `list` stays flat, in display order, so the arrow keys walk the groups
  // top to bottom; a group's heading is drawn above its first project.
  const list = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const all = projects ?? [];
    const hits = needle
      ? all.filter((p) => `${p.name} ${(p.services ?? []).join(" ")}`.toLowerCase().includes(needle))
      : all;
    return hits
      .map((p) => ({ p, g: switcherGroup(p) }))
      .sort((a, b) => a.g - b.g || a.p.name.localeCompare(b.p.name, undefined, { sensitivity: "base", numeric: true }));
  }, [projects, q]);

  const currentSvc = (projects?.find((p) => p.id === projectId)?.services ?? []).join(", ");

  function toggle() {
    if (!open && btnRef.current) {
      // Fixed, not absolute: the bar's left cluster clips its overflow so long
      // names don't shove the tabs off screen, and it would clip this too.
      // Kept on screen: under the title, but nudged left on a narrow window.
      const r = btnRef.current.getBoundingClientRect();
      const w = Math.min(380, window.innerWidth - 24);
      setPos({ top: r.bottom + 6, left: Math.max(12, Math.min(r.left, window.innerWidth - w - 12)) });
      setQ("");
      setHi(0);
    }
    setOpen((v) => !v);
  }

  function go(p: Project) {
    setOpen(false);
    if (p.id !== projectId) navigate(`/projects/${p.id}${tabPath(active)}`);
  }

  function onKey(e: KeyboardEvent) {
    if (e.key === "Escape") { setOpen(false); btnRef.current?.focus(); }
    else if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(h + 1, list.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter" && list[hi]) { e.preventDefault(); go(list[hi].p); }
  }

  return (
    <>
      <button ref={btnRef} type="button" className="topbar-name proj-switch-btn"
        aria-haspopup="listbox" aria-expanded={open} title="Switch to another project" onClick={toggle}>
        <span className="proj-switch-name">{name}</span>
        {currentSvc && <span className="proj-switch-svc proj-switch-btn-svc" title={`Service: ${currentSvc}`}>{currentSvc}</span>}
        <Icon name="chevron-down" size={14} />
      </button>
      {open && (
        <>
          <div className="menu-backdrop" onClick={() => setOpen(false)} />
          <div className="proj-switch" style={{ top: pos.top, left: pos.left }} onKeyDown={onKey}>
            <input className="proj-switch-q" autoFocus placeholder="Find a project…" value={q}
              aria-label="Find a project" onChange={(e) => { setQ(e.target.value); setHi(0); }} />
            <div className="proj-switch-list" role="listbox" aria-label="Projects">
              {!projects && <div className="proj-switch-empty">Loading…</div>}
              {projects && !list.length && <div className="proj-switch-empty">No project matches “{q}”.</div>}
              {list.map(({ p, g }, i) => {
                const svc = (p.services ?? []).join(", ");
                const head = i === 0 || list[i - 1].g !== g ? GROUPS[g] : null;
                return (
                  <Fragment key={p.id}>
                    {head && (
                      <div className="proj-switch-group" role="presentation">
                        <span className="proj-switch-dot" style={head.dot ? { background: head.dot } : undefined} />
                        {head.label}
                      </div>
                    )}
                    <button type="button" role="option" aria-selected={p.id === projectId}
                      className={`proj-switch-item${i === hi ? " hi" : ""}${p.id === projectId ? " on" : ""}`}
                      onMouseEnter={() => setHi(i)} onClick={() => go(p)}>
                      <span className="proj-switch-item-name">{p.name}</span>
                      {svc && <span className="proj-switch-svc">{svc}</span>}
                      {p.id === projectId && <Icon name="check" size={14} />}
                    </button>
                  </Fragment>
                );
              })}
            </div>
          </div>
        </>
      )}
    </>
  );
}
